import { Search, Loader2 } from 'lucide-react';
import { useState, type FormEvent, type ReactNode } from 'react';
import { cn } from '@/lib/cn';
import type { LookupParams } from '@/lib/bootstrap';
import type { BusinessClientSummary } from '@/types';
import { ClientSelect } from './ClientSelect';
import { GameSelect } from './GameSelect';

/**
 * The lookup surface. Four identity types, mirroring the package's contract:
 *   PT1  play_service_id + game_uuid + bet_id
 *   PT2  play_service_id + transaction_id  (bet, win OR rollback trx)
 *   PT3  play_service_id + cycle_uniq_id   (the bet-level hash)
 *   PT4  play_service_id + tr_uniq_id      (the per-transaction hash)
 */
type Mode = 'pt1' | 'pt2' | 'pt3' | 'pt4' | 'pt5';

const MODES: { id: Mode; label: string; title: string }[] = [
  { id: 'pt1', label: 'by bet', title: 'Look up by game + bet id' },
  { id: 'pt2', label: 'by trx', title: 'Look up by transaction id' },
  { id: 'pt3', label: 'cycle', title: 'Look up by cycle_uniq_id (the bet-level hash)' },
  { id: 'pt4', label: 'tr hash', title: 'Look up by tr_uniq_id (the per-transaction hash)' },
  { id: 'pt5', label: 'by round', title: 'Look up a whole round (blackjack) by game + round id' },
];

function initialMode(l: LookupParams | null): Mode {
  if (l?.round_id) return 'pt5';
  if (l?.tr_uniq_id) return 'pt4';
  if (l?.cycle_uniq_id) return 'pt3';
  if (l?.transaction_id) return 'pt2';
  return 'pt1';
}

export function CommandBar({
  initial,
  loading,
  clients,
  clientsLoading,
  clientsError,
  onLookup,
  sticky = true,
  submitLabel = 'Inspect',
  lead,
  api,
  token,
}: {
  initial: LookupParams | null;
  loading: boolean;
  clients: BusinessClientSummary[];
  clientsLoading: boolean;
  clientsError: boolean;
  onLookup: (params: LookupParams) => void;
  /** Sticky under the navbar (Bet Inspection). Off when embedded in a bigger bar. */
  sticky?: boolean;
  submitLabel?: string;
  /** Rendered before the form — e.g. a lane badge in the Compare bar. */
  lead?: ReactNode;
  /** For the PT1 game picker to list a project's games. Manual entry works without them. */
  api?: string;
  token?: string | null;
}) {
  const [mode, setMode] = useState<Mode>(() => initialMode(initial));
  const [psid, setPsid] = useState(initial?.play_service_id ?? '');
  const [gameUuid, setGameUuid] = useState(initial?.game_uuid ?? '');
  const [betId, setBetId] = useState(initial?.bet_id ?? '');
  const [trxId, setTrxId] = useState(initial?.transaction_id ?? '');
  const [cycleId, setCycleId] = useState(initial?.cycle_uniq_id ?? '');
  const [trUniqId, setTrUniqId] = useState(initial?.tr_uniq_id ?? '');
  const [roundId, setRoundId] = useState(initial?.round_id ?? '');

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const p = psid.trim();
    if (!p) return;
    const byMode: Record<Mode, LookupParams> = {
      pt1: { play_service_id: p, game_uuid: gameUuid.trim(), bet_id: betId.trim() },
      pt2: { play_service_id: p, transaction_id: trxId.trim() },
      pt3: { play_service_id: p, cycle_uniq_id: cycleId.trim() },
      pt4: { play_service_id: p, tr_uniq_id: trUniqId.trim() },
      pt5: { play_service_id: p, game_uuid: gameUuid.trim(), round_id: roundId.trim() },
    };
    onLookup(byMode[mode]);
  };

  const canSubmit = Boolean(
    psid.trim() &&
      {
        pt1: gameUuid.trim() && betId.trim(),
        pt2: trxId.trim(),
        pt3: cycleId.trim(),
        pt4: trUniqId.trim(),
        pt5: gameUuid.trim() && roundId.trim(),
      }[mode],
  );

  const inner = (
    <div className="flex items-center gap-3 px-4 py-2.5">
      {lead}
      <form onSubmit={submit} className="flex flex-1 flex-wrap items-center gap-1.5">
          <ModeToggle mode={mode} onChange={setMode} />

          <ClientSelect
            value={psid}
            onChange={setPsid}
            clients={clients}
            loading={clientsLoading}
            error={clientsError}
          />
          {mode === 'pt1' && (
            <>
              <GameSelect
                value={gameUuid}
                onChange={setGameUuid}
                api={api ?? ''}
                token={token ?? null}
                playServiceId={psid}
              />
              <Field label="bet_id" value={betId} onChange={setBetId} width="w-[110px]" />
            </>
          )}
          {mode === 'pt2' && (
            <Field label="transaction_id" value={trxId} onChange={setTrxId} width="w-[160px]" />
          )}
          {mode === 'pt3' && (
            <Field label="cycle_uniq_id" value={cycleId} onChange={setCycleId} width="w-[300px]" />
          )}
          {mode === 'pt4' && (
            <Field label="tr_uniq_id" value={trUniqId} onChange={setTrUniqId} width="w-[300px]" />
          )}
          {mode === 'pt5' && (
            <>
              <GameSelect
                value={gameUuid}
                onChange={setGameUuid}
                api={api ?? ''}
                token={token ?? null}
                playServiceId={psid}
              />
              <Field label="round_id" value={roundId} onChange={setRoundId} width="w-[110px]" />
            </>
          )}

          <button
            type="submit"
            disabled={!canSubmit || loading}
            className={cn(
              'ml-1 inline-flex items-center gap-1.5 border border-accent/40 bg-accent/10 px-3 py-1.5',
              'font-condensed text-[11px] font-semibold uppercase tracking-wider text-accent',
              'hover:bg-accent/20 disabled:cursor-not-allowed disabled:opacity-40',
            )}
          >
            {loading ? (
              <Loader2 size={12} className="animate-spin" />
            ) : (
              <Search size={12} />
            )}
            {submitLabel}
          </button>
        </form>
    </div>
  );

  if (!sticky) return inner;

  // Sits directly under the global navbar (h ≈ 41px), so the lookup bar stays
  // reachable while a long bundle scrolls.
  return (
    <div className="sticky top-[41px] z-30 border-b border-hairline bg-bg/95 backdrop-blur">
      {inner}
    </div>
  );
}

function ModeToggle({ mode, onChange }: { mode: Mode; onChange: (m: Mode) => void }) {
  return (
    <div className="flex border border-hairline">
      {MODES.map((m) => (
        <button
          key={m.id}
          type="button"
          onClick={() => onChange(m.id)}
          title={m.title}
          className={cn(
            'px-2 py-1.5 font-condensed text-[10px] font-semibold uppercase tracking-wider',
            mode === m.id
              ? 'bg-surface-raised text-accent'
              : 'text-text-faint hover:text-text-dim',
          )}
        >
          {m.label}
        </button>
      ))}
    </div>
  );
}

function Field({
  label,
  value,
  onChange,
  width,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  width: string;
}) {
  return (
    <label className={cn('relative block', width)}>
      <span className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 font-condensed text-[9px] uppercase tracking-wider text-text-faint">
        {label}
      </span>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        spellCheck={false}
        autoComplete="off"
        className={cn(
          'w-full border border-hairline bg-surface-sunken py-1.5 pl-[76px] pr-2',
          'font-mono text-[12px] text-text placeholder:text-text-faint',
          'hover:border-hairline-strong focus:border-accent focus:outline-none',
        )}
        style={{ paddingLeft: `${label.length * 5 + 16}px` }}
      />
    </label>
  );
}

